package com.htmlagent.site.state.brief

/**
 * The vocabulary the assistant collects, and the only part of it that is structure rather than
 * wording: identifiers, budgets and what each choice allows.
 *
 * Every label a person reads lives in `i18n/strings.json`; what is here is what the code has to
 * reason about. Keeping them apart is what lets the assistant speak two languages without a
 * `when` over the locale in the middle of a rule.
 */

/** Which of the four questions a value came from, so a skipped one can be left out. */
enum class BriefStep {
    Commission,
    Register,
    Colour,
    Form,
    ;

    /** What the person sees on the step pill. Derived so the two can never drift apart. */
    val number: Int get() = ordinal + 1

    /** The one after this, or this one when it is the last: the wizard never runs off the end. */
    val next: BriefStep get() = entries.getOrElse(ordinal + 1) { this }

    /** The one before this, or this one when it is the first. */
    val previous: BriefStep get() = entries.getOrElse(ordinal - 1) { this }
}

/** What the visitor has to do. A landing gets one; the rest bow to it. */
enum class CallToAction(val id: String) {
    Visit("visit"),
    Book("book"),
    Buy("buy"),
    Write("write"),
    Subscribe("subscribe"),
}

/**
 * What kind of page it is. It decides the structure and which actions make sense, never the
 * looks: a warm register and a technical one both fit a menu.
 *
 * [Landing] is the default because it is what nearly every commission turns out to be, and
 * because it is the only kind whose rule — one action, repeated — orders the whole page.
 */
enum class PageKind(val id: String, val actions: List<CallToAction>) {
    Landing(
        id = "landing",
        actions = listOf(
            CallToAction.Visit, CallToAction.Book, CallToAction.Buy,
            CallToAction.Write, CallToAction.Subscribe,
        ),
    ),
    Menu(
        id = "menu",
        actions = listOf(CallToAction.Visit, CallToAction.Book, CallToAction.Buy),
    ),
    Event(
        id = "event",
        actions = listOf(CallToAction.Book, CallToAction.Buy, CallToAction.Subscribe),
    ),
    Portfolio(
        id = "portfolio",
        actions = listOf(CallToAction.Write, CallToAction.Visit, CallToAction.Subscribe),
    ),
    Profile(
        id = "profile",
        actions = listOf(CallToAction.Write, CallToAction.Visit, CallToAction.Subscribe),
    ),
    Waiting(
        id = "waiting",
        actions = listOf(CallToAction.Subscribe, CallToAction.Write),
    ),
    ;

    /** The preselected one, marked as such so it does not read as an arbitrary choice. */
    val isDefault: Boolean get() = this == Landing

    companion object {
        val default: PageKind = Landing
    }
}

/** How much a register lets the page move. */
enum class MotionLevel(val id: String) { None("none"), Low("low"), Medium("medium"), High("high") }

/** How tightly the page packs. */
enum class Density(val id: String) { Airy("airy"), Medium("medium"), Dense("dense") }

/**
 * The register, which is a budget and not a label.
 *
 * "One accent", "one gesture and only one" are right for a tax advisory and false for an
 * electronic music festival: in an expressive register the accumulation *is* the work. The
 * numbers here are the ones `skills/visual-direction` grades everything else by, so the
 * assistant can show the consequence of the choice instead of describing it.
 *
 * What no row grades: text contrast, visible focus, reduced motion and touch target size.
 */
@Suppress("MagicNumber") // The budget table of skills/visual-direction: the numbers are the data.
enum class Register(val id: String, val budget: Budget, val pairings: List<TypePairing>) {
    Editorial(
        "editorial", Budget(1, 1, MotionLevel.Low, 1, Density.Airy),
        listOf(
            TypePairing("Fraunces", "Karla"),
            TypePairing("Instrument Serif", "Work Sans"),
            TypePairing("Newsreader", "Public Sans"),
        ),
    ),
    Technical(
        "technical", Budget(1, 1, MotionLevel.None, 1, Density.Dense),
        listOf(
            TypePairing("Space Grotesk", "IBM Plex Sans"),
            TypePairing("IBM Plex Sans", "IBM Plex Sans"),
            TypePairing("Chivo", "Public Sans"),
        ),
    ),
    Warm(
        "warm", Budget(2, 1, MotionLevel.Low, 2, Density.Medium),
        listOf(
            TypePairing("Bricolage Grotesque", "Nunito Sans"),
            TypePairing("Fraunces", "Nunito Sans"),
            TypePairing("Bitter", "Karla"),
        ),
    ),
    Institutional(
        "institutional", Budget(1, 1, MotionLevel.None, 1, Density.Medium),
        listOf(
            TypePairing("Libre Baskerville", "Source Sans 3"),
            TypePairing("Source Serif 4", "Source Sans 3"),
            TypePairing("Archivo", "Archivo"),
        ),
    ),
    Expressive(
        "expressive", Budget(3, 2, MotionLevel.High, 2, Density.Airy),
        listOf(
            TypePairing("Syne", "Manrope"),
            TypePairing("Unbounded", "DM Sans"),
            TypePairing("Anton", "Karla"),
        ),
    ),
    Material(
        "material", Budget(2, 1, MotionLevel.Medium, 2, Density.Medium),
        listOf(
            TypePairing("Outfit", "Inter"),
            TypePairing("Sora", "Inter"),
            TypePairing("Manrope", "Manrope"),
        ),
    ),
    ;

    companion object {
        val default: Register = Warm
    }
}

/**
 * How much a register lets the page accumulate.
 *
 * "One accent", "one gesture and only one" are right for a tax advisory and false for an
 * electronic music festival. The discipline is not removed, it is graded — and this is the
 * grading, so a choice of register can show its consequence instead of describing it.
 */
data class Budget(
    val accents: Int,
    val gestures: Int,
    val motion: MotionLevel,
    val radii: Int,
    val density: Density,
)

/**
 * A display face and a body face, both verified in `skills/css-system/typefaces.md`.
 *
 * The fallback is derived and not stored because it follows from the face's class, and it is
 * what the visitor reads while the web font loads — or for good, if it never arrives.
 */
data class TypePairing(val display: String, val body: String) {

    /** The full stack for the display face, ready to travel in the brief. */
    val displayStack: String get() = stackFor(display)

    /** The full stack for the body face. */
    val bodyStack: String get() = stackFor(body)

    private fun stackFor(face: String): String =
        if (face in SERIFS) "\"$face\", Georgia, serif" else "\"$face\", \"Helvetica Neue\", Arial, sans-serif"

    private companion object {
        val SERIFS = setOf(
            "Fraunces", "Instrument Serif", "Newsreader", "Bitter",
            "Libre Baskerville", "Source Serif 4", "Lora", "Spectral",
            "EB Garamond", "Crimson Pro",
        )
    }
}

/**
 * Where the page's visuals come from.
 *
 * It is asked because it is the presence floor: text and rules alone produce a memo, and the
 * skill that says so is the one the agent skips most often. [Built] is first because the
 * environment favours it — a gradient or a piece of geometry costs no request and cannot come
 * back showing the wrong thing.
 */
enum class ImagerySource(val id: String) {
    Built("built"),
    Photography("photography"),
    None("none"),
    ;

    /** Only a photograph has a treatment to decide. */
    val hasTreatment: Boolean get() = this == Photography
}

/**
 * What is done to a photograph, which is a decision about discipline and not about the photo.
 *
 * [Veiled] carries the rule that makes it safe: the veil is calibrated against pure white, not
 * against the photograph you are imagining. Calibrated the other way, a lighter photo arrives
 * tomorrow and the text disappears.
 */
enum class ImageryTreatment(val id: String) {
    FullColour("full-colour"),
    GreyToColour("grey-to-colour"),
    Veiled("veiled"),
}

/** Whether a gesture is a decision about composition or about behaviour. */
enum class GestureKind(val id: String) { Composition("composition"), Behaviour("behaviour") }

/**
 * The one visual idea this page has and others do not.
 *
 * With a budget of one it is composition **or** behaviour, never both. With a budget of two —
 * only expressive — they must be of different kinds: two animations are one gesture repeated.
 */
enum class Gesture(val id: String, val kind: GestureKind) {
    BrokenGrid("broken-grid", GestureKind.Composition),
    GiantFigures("giant-figures", GestureKind.Composition),
    ColourBleed("colour-bleed", GestureKind.Composition),
    Overlap("overlap", GestureKind.Composition),
    BuildOnEntry("build-on-entry", GestureKind.Behaviour),
    CursorScene("cursor-scene", GestureKind.Behaviour),
}
